import React, { useState } from "react";
import { ArrowLeft, Clipboard, EnvelopePlus, JournalText, Pencil } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import { BaseButton } from "../../../libs";
import {
  ReferralNote,
  ReferralParticipant,
  ReferralParticipantActivity,
  ReferralProgram,
} from "../types";
import { InviteReferrerModal } from "./invite-referrer-modal/InviteReferrerModal";
import { ParticipantActivityModal } from "./participant-activity-modal/ParticipantActivityModal";
import { ParticipantNotesModal } from "./participant-notes-modal/ParticipantNotesModal";
import * as Styled from "./ReferralParticipantsPanel.styled";

interface Props {
  program: ReferralProgram;
  participants: ReferralParticipant[];
  onBack: () => void;
  onEdit: () => void;
  onInvite: (data: { email: string; invitationUrl?: string }) => Promise<void>;
  onAddDestination: (url: string) => Promise<void>;
  onGetActivity: (participantId: string) => Promise<ReferralParticipantActivity>;
  onAddNote: (participantId: string, text: string) => Promise<ReferralNote>;
  onDeleteNote: (participantId: string, noteId: string) => Promise<void>;
}

const referralLink = (base: string, code: string) => {
  const url = new URL(base);
  url.searchParams.set("ref", code);
  return url.toString();
};

export const ReferralParticipantsPanel: React.FC<Props> = ({
  program,
  participants,
  onBack,
  onEdit,
  onInvite,
  onAddDestination,
  onGetActivity,
  onAddNote,
  onDeleteNote,
}) => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [activityParticipant, setActivityParticipant] = useState<ReferralParticipant | null>(null);
  const [notesParticipantId, setNotesParticipantId] = useState<string | null>(null);
  const destinations = program.destinationUrls || [];
  const notesParticipant =
    participants.find((participant) => participant.id === notesParticipantId) || null;

  const copy = async (participant: ReferralParticipant) => {
    if (!destinations[0]) return;
    await navigator.clipboard.writeText(referralLink(destinations[0], participant.referralCode));
    toast.success("Referral link copied");
  };

  return (
    <Styled.Panel>
      <Styled.Header>
        <Styled.HeaderLeft>
          <Styled.BackButton onClick={onBack} title="Back to programs">
            <ArrowLeft />
          </Styled.BackButton>
          <Styled.HeaderCopy>
            <Styled.Title>{program.name} Referrers</Styled.Title>
            <Styled.Subtitle>
              Invited and registered referrers, with aggregate registration and purchase totals.
            </Styled.Subtitle>
          </Styled.HeaderCopy>
        </Styled.HeaderLeft>
        <Styled.HeaderActions>
          <BaseButton variant="secondary" onClick={onEdit}>
            <Styled.ButtonContent>
              <Pencil /> Edit Program
            </Styled.ButtonContent>
          </BaseButton>
          <BaseButton onClick={() => setShowInviteModal(true)}>
            <Styled.ButtonContent>
              <EnvelopePlus /> Invite Referrer
            </Styled.ButtonContent>
          </BaseButton>
        </Styled.HeaderActions>
      </Styled.Header>

      {participants.length === 0 ? (
        <Styled.EmptyState>No referrers have joined or been invited yet.</Styled.EmptyState>
      ) : (
        <Styled.TableWrapper>
          <Styled.Table>
            <thead>
              <tr>
                <th>Referrer</th>
                <th>Status</th>
                <th>Referral code</th>
                <th>Registrations</th>
                <th>Purchases</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((participant) => (
                <tr key={participant.id}>
                  <td>
                    <Styled.UserButton
                      onClick={() => setActivityParticipant(participant)}
                      title="View registrations and purchases"
                    >
                      {participant.email}
                    </Styled.UserButton>
                  </td>
                  <td>
                    <Styled.Status $active={participant.status === "ACTIVE"}>
                      {participant.status === "ACTIVE" ? "Registered" : "Unregistered"}
                    </Styled.Status>
                  </td>
                  <td>
                    <Styled.Code>{participant.referralCode}</Styled.Code>
                  </td>
                  <td>
                    <Styled.CountButton onClick={() => setActivityParticipant(participant)}>
                      {participant.registrationsCount || 0}
                    </Styled.CountButton>
                  </td>
                  <td>
                    <Styled.CountButton onClick={() => setActivityParticipant(participant)}>
                      {participant.purchasesCount || 0}
                    </Styled.CountButton>
                  </td>
                  <td>
                    <Styled.RowActions>
                      <Styled.CopyButton
                        onClick={() => copy(participant)}
                        disabled={!destinations.length}
                      >
                        <Clipboard /> Copy Link
                      </Styled.CopyButton>
                      <Styled.NotesButton onClick={() => setNotesParticipantId(participant.id)}>
                        <JournalText /> Notes
                        {participant.notes?.length ? ` (${participant.notes.length})` : ""}
                      </Styled.NotesButton>
                    </Styled.RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Styled.Table>
        </Styled.TableWrapper>
      )}

      <InviteReferrerModal
        show={showInviteModal}
        onHide={() => setShowInviteModal(false)}
        program={program}
        onInvite={onInvite}
        onAddDestination={onAddDestination}
      />
      <ParticipantActivityModal
        show={!!activityParticipant}
        onHide={() => setActivityParticipant(null)}
        participant={activityParticipant}
        onLoad={onGetActivity}
      />
      <ParticipantNotesModal
        show={!!notesParticipant}
        onHide={() => setNotesParticipantId(null)}
        participant={notesParticipant}
        onAdd={onAddNote}
        onDelete={onDeleteNote}
      />
    </Styled.Panel>
  );
};
